import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { usd } from "../lib/money";
import { BOOK_LABEL } from "./Performance";
import { Money, MoneySwitch, ScopeNote } from "./MoneyScope";

// "What's really happening with forex vs. crypto" (Alan, 2026-08-18). One
// card per sleeve: the NET number (realized + open − fees − financing), a
// one-sentence verdict, the cost lines that were invisible before, and the
// per-book split. Costs come from each venue's own cash feed; the curve is
// the broker's own EOD equity where a venue is single-sleeve (Kraken →
// crypto, OANDA → forex), net of funding.

type SleeveBook = {
  book: string; realized: number; unrealized: number; trades: number; wins: number; losses: number;
  win_rate: number; avg_win: number; avg_loss: number; profit_factor: number | null; max_drawdown: number;
  budget: number | null; deployed: number | null; open_positions: number;
};
type Sleeve = {
  sleeve: string; realized: number; unrealized: number; fees: number; financing: number; net: number;
  trades: number; wins: number; losses: number; win_rate: number; budget: number; deployed: number;
  books: SleeveBook[]; equity_curve: { date: string; value: number }[]; net_funding: number;
  curve_source: string; verdict: string;
  recent: { symbol: string; book: string; pnl: number; exit_time: string | null; exit_reason: string | null }[];
};
type Totals = { realized: number; unrealized: number; costs: number; net: number; note: string };
type Resp = { period: string; since: string | null; sleeves: Sleeve[]; notes: string[];
  is_paper: boolean; scope_books: string[]; totals: Totals };

const PERIODS = ["today", "7d", "30d", "90d", "all"] as const;
type Period = (typeof PERIODS)[number];
const SLEEVE_LABEL: Record<string, string> = { equity: "Equities", option: "Options", crypto: "Crypto", forex: "Forex", commodity: "Commodities" };

export default function SleevePerformance({ book }: { book: string }) {
  const [period, setPeriod] = useState<Period>("30d");
  const [money, setMoney] = useState<Money>("real");
  const [data, setData] = useState<Resp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    api.get<Resp>(`/api/performance/sleeves?period=${period}&book=${book}&money=${money}`)
      .then((r) => { if (live) { setData(r); setErr(null); } })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [period, book, money]);

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="label mb-0">By sleeve <span className="normal-case text-slate-500">— what's really happening, net of costs</span></h2>
          {data && <ScopeNote book={book} money={money} isPaper={data.is_paper} books={data.scope_books} />}
        </div>
        <MoneySwitch book={book} money={money} onChange={setMoney} />
        <div className="flex gap-1 text-xs font-mono">
          {PERIODS.map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-2 py-0.5 rounded border ${period === p ? "border-slate-400 text-slate-100" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      {data?.totals && <SleeveTotals t={data.totals} period={data.period} />}
      {err && <div className="text-down text-sm mt-2">{err}</div>}
      {!data && !err && <div className="text-slate-600 text-sm mt-3">reading every venue…</div>}
      {data && data.sleeves.every((s) => s.trades === 0 && Math.abs(s.unrealized) < 0.005) && (
        <div className="text-slate-600 text-sm mt-3">nothing traded in this scope</div>
      )}
      {data && (
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {data.sleeves.map((s) => (
            <SleeveCard key={s.sleeve} s={s} open={open === s.sleeve} onToggle={() => setOpen(open === s.sleeve ? null : s.sleeve)} />
          ))}
        </div>
      )}
      {data && (
        <details className="mt-3 text-[10px] text-slate-600">
          <summary className="cursor-pointer">how these numbers are built</summary>
          <ul className="list-disc ml-4 mt-1 space-y-0.5">{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

// The bridge to the page headline directly above this card. Same word, two
// meanings: the headline banks only what closed, these cards mark what is
// still open. Spell the identity out rather than leave it to be inferred.
function SleeveTotals({ t, period }: { t: Totals; period: string }) {
  const Cell = ({ k, v, sub }: { k: string; v: number; sub?: string }) => (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{k}</div>
      <div className={`font-mono text-sm font-bold ${v >= 0 ? "text-up" : "text-down"}`}>{usd(v)}</div>
      {sub && <div className="text-[9px] text-slate-600">{sub}</div>}
    </div>
  );
  return (
    <div className="mt-2 rounded border border-slate-800 px-3 py-2">
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Cell k="realized" v={t.realized} sub="banked — matches the headline" />
        <span className="text-slate-600 font-mono">+</span>
        <Cell k="open" v={t.unrealized} sub="still held, not banked" />
        <span className="text-slate-600 font-mono">−</span>
        <Cell k="costs" v={-t.costs} sub="fees + financing" />
        <span className="text-slate-600 font-mono">=</span>
        <Cell k={`total, ${period}`} v={t.net} sub="what the cards below sum to" />
      </div>
      <p className="text-[10px] text-slate-600 text-center mt-1 leading-snug">{t.note}</p>
    </div>
  );
}

function SleeveCard({ s, open, onToggle }: { s: Sleeve; open: boolean; onToggle: () => void }) {
  const quiet = s.trades === 0 && Math.abs(s.unrealized) < 0.005 && s.fees + s.financing < 0.005;
  const costs = s.fees + s.financing;
  return (
    <div className={`rounded border p-3 ${quiet ? "border-slate-800 opacity-70" : "border-slate-700"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">{SLEEVE_LABEL[s.sleeve] ?? s.sleeve}</div>
          <div className={`text-2xl font-black font-mono leading-none mt-1 ${s.net >= 0 ? "text-up" : "text-down"}`}>{usd(s.net)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            net <span className="text-slate-600">incl. open</span> · {s.trades} round-trip{s.trades === 1 ? "" : "s"} · {s.win_rate}% hit
          </div>
        </div>
        <Spark curve={s.equity_curve} funding={s.net_funding} />
      </div>
      <p className="text-xs text-slate-300 mt-2 leading-snug">{s.verdict}</p>
      <div className="grid grid-cols-4 gap-1 mt-2 text-[11px] font-mono">
        <Kv k="realized" v={s.realized} />
        <Kv k="open" v={s.unrealized} />
        <Kv k="fees" v={-s.fees} muted={s.fees < 0.005} />
        <Kv k="financing" v={-s.financing} muted={s.financing < 0.005} />
      </div>
      {(s.deployed > 0 || s.budget > 0) && (
        <div className="text-[10px] text-slate-500 mt-1 font-mono">
          deployed {usd(s.deployed)} of {usd(s.budget)} budget
          {costs >= 0.005 && s.deployed > 0 && <> · costs {((costs / s.deployed) * 100).toFixed(2)}% of deployed</>}
        </div>
      )}
      {s.books.length > 0 && (
        <button onClick={onToggle} className="text-[10px] text-slate-500 hover:text-slate-300 mt-2 underline-offset-2 hover:underline">
          {open ? "hide" : "show"} {s.books.length} book{s.books.length === 1 ? "" : "s"}
        </button>
      )}
      {open && (
        <div className="mt-1 overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-left text-slate-500 uppercase text-[9px]">
                <th className="py-0.5">Book</th><th>Realized</th><th>Open</th><th>Trades</th><th>Hit</th><th>PF</th><th>Deployed</th><th>Pos</th>
              </tr>
            </thead>
            <tbody>
              {s.books.map((b) => (
                <tr key={b.book} className="border-t border-slate-800">
                  <td className="py-0.5 text-slate-300">{BOOK_LABEL[b.book] ?? b.book}</td>
                  <td className={b.realized >= 0 ? "text-up" : "text-down"}>{usd(b.realized)}</td>
                  <td className={b.unrealized >= 0 ? "text-up" : "text-down"}>{usd(b.unrealized)}</td>
                  <td>{b.trades}</td>
                  <td>{b.trades ? `${b.win_rate}%` : "—"}</td>
                  <td>{b.profit_factor != null ? b.profit_factor.toFixed(2) : "—"}</td>
                  <td className="text-slate-400">{b.deployed != null ? usd(b.deployed) : "—"}</td>
                  <td>{b.open_positions}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.recent.length > 0 && (
            <div className="mt-2 text-[10px] text-slate-500">
              <div className="uppercase text-[9px]">Recent closes</div>
              {s.recent.map((r, i) => (
                <div key={i} className="flex gap-2 font-mono">
                  <span className="w-24 truncate text-slate-400">{r.symbol}</span>
                  <span className="w-20 truncate">{BOOK_LABEL[r.book] ?? r.book}</span>
                  <span className={`w-16 text-right ${r.pnl >= 0 ? "text-up" : "text-down"}`}>{usd(r.pnl)}</span>
                  <span className="truncate text-slate-600">{r.exit_reason ?? ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kv({ k, v, muted }: { k: string; v: number; muted?: boolean }) {
  const tone = muted ? "text-slate-600" : v >= 0 ? "text-up" : "text-down";
  return (
    <div>
      <div className="text-[9px] uppercase text-slate-500">{k}</div>
      <div className={tone}>{Math.abs(v) < 0.005 ? "—" : usd(v)}</div>
    </div>
  );
}

function Spark({ curve, funding }: { curve: { date: string; value: number }[]; funding: number }) {
  const W = 120, H = 36, pad = 2;
  const pts = useMemo(() => {
    if (curve.length < 2) return null;
    const ys = curve.map((c) => c.value);
    const min = Math.min(...ys), max = Math.max(...ys), span = max - min || 1;
    const n = curve.length;
    const x = (i: number) => pad + (i / (n - 1)) * (W - 2 * pad);
    const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad);
    const chg = curve[n - 1].value - curve[0].value - funding;
    return { line: curve.map((c, i) => `${x(i)},${y(c.value)}`).join(" "), chg };
  }, [curve, funding]);
  if (!pts) return null;
  const up = pts.chg >= 0;
  return (
    <div className="text-right">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
        <polyline points={pts.line} fill="none" stroke={up ? "#2ec4b6" : "#ff3b4d"} strokeWidth={1.5} />
      </svg>
      <div className="text-[9px] text-slate-500">broker equity {curve[0].date.slice(5)}→{curve[curve.length - 1].date.slice(5)}{Math.abs(funding) >= 0.005 ? " (net of funding)" : ""}</div>
    </div>
  );
}
